import { useState } from 'react'
import { NavLink as RouterLink } from 'react-router-dom'
import { Box, Button, Collapse, ListItem } from '@mui/material'
import useSettings from '../hooks/useSettings'
import ChevronDownIcon from '../icons/ChevronDown'
import ChevronRightIcon from '../icons/ChevronRight'

export interface NavItemProps {
	active?: boolean
	children?: React.ReactNode
	depth: number
	icon?: React.ReactNode
	info?: React.ReactNode
	open?: boolean
	path?: string
	title: string
}

const NavItem = ({
	active = false,
	open: openProp = false,
	children,
	depth,
	icon,
	info,
	path,
	title,
	...other
}: NavItemProps) => {
	const [open, setOpen] = useState<boolean>(openProp)
	const { settings } = useSettings()

	const handleToggle = () => {
		setOpen((prevOpen) => !prevOpen)
	}

	let paddingLeft = depth > 0 ? 32 + 8 * depth : 16

	const activeColor = settings.theme === 'dark' ? 'text.mint' : 'text.purple'

	const buttonContent = (
		<>
			<Box
				sx={{
					flexGrow: 1,
					display: 'flex',
					alignItems: 'center'
				}}
			>
				{title}
				{icon && <Box sx={{ ml: 3 }}>{icon}</Box>}
			</Box>
			{info}
		</>
	)

	// Branch
	if (children) {
		return (
			<ListItem disableGutters sx={{ display: 'block', py: 0 }} {...other}>
				<Button
					endIcon={
						!open ? <ChevronRightIcon fontSize="small" /> : <ChevronDownIcon fontSize="small" />
					}
					onClick={handleToggle}
					sx={{
						color: 'text.secondary',
						fontWeight: 'fontWeightMedium',
						justifyContent: 'flex-start',
						pl: `${paddingLeft}px`,
						pr: '8px',
						py: '12px',
						textAlign: 'left',
						textTransform: 'none',
						width: '100%'
					}}
					variant="text"
				>
					{buttonContent}
				</Button>
				<Collapse in={open}>{children}</Collapse>
			</ListItem>
		)
	}

	// Leaf
	return (
		<ListItem disableGutters sx={{ display: 'flex', py: 0 }}>
			<Button
				component={path ? RouterLink : 'button'}
				to={path}
				sx={{
					fontWeight: 'fontWeightMedium',
					justifyContent: 'flex-start',
					textAlign: 'left',
					pl: `${paddingLeft}px`,
					pr: '8px',
					py: '12px',
					textTransform: 'none',
					width: '100%',
					...(active && {
						color: activeColor,
						fontWeight: 600,
						'& svg': {
							color: activeColor
						}
					})
				}}
				variant="text"
			>
				{buttonContent}
			</Button>
		</ListItem>
	)
}

export default NavItem
